import type { CSSProperties } from "react";

// ── OpenEstimate Theme System ──────────────────────────────────────────────
// Two complete themes: dark (default) and light ("Warm Technical").
// Both share the teal accent #4fd1c5 / #0a8f83 as the brand anchor.
//
// Token access patterns:
//   Existing flat keys (backward-compat):  T.bgApp, T.borderAccent, T.textMuted …
//   New grouped tokens (design system):    T.accent.teal, T.shadow.md, T.space[6] …
//
// NEVER introduce CSS files, Tailwind, or className styling.
// Every new token lands here — not in a component.

// ── Shared scale constants (theme-independent) ────────────────────────────
// ── Corner radii ───────────────────────────────────────────────────────────
// Four steps. 2 vs 3 vs 4 is imperceptible on a single element but reads as
// sloppiness across a screen, so the near-duplicates are collapsed.
//
// `pill` is 999 rather than half-the-height: a literal radius silently stops
// being a pill the moment the element's height changes.
//
// Enforced by theme.test.ts alongside the type scale.
export const RADIUS = {
  sm:   3,    // inputs, chips, badges, small controls
  md:   6,    // cards, panels, dropdowns
  lg:   10,   // modals, overlays
  pill: 999,  // toggles, avatars, fully-rounded chips
} as const;

export const RADIUS_SCALE: readonly number[] = Object.values(RADIUS);

export const SPACE = {
  1: 4,  2: 8,  3: 12, 4: 16, 5: 20, 6: 24,
  8: 32, 10: 40, 12: 48, 16: 64, 20: 80,
} as const;

export const SIZE = {
  10: 10, 11: 11, 12: 12, 13: 13, 14: 14, 15: 15, 16: 16,
  18: 18, 20: 20, 24: 24, 28: 28, 32: 32, 38: 38, 48: 48, 56: 56,
} as const;

// ── Type scale (application UI) ────────────────────────────────────────────
// Nine steps, deliberately sparse. Sizes a reader can't tell apart don't
// create hierarchy — they just read as inconsistency — so near-duplicates
// (8/9, 14/15, 17/18, 22, 26/28) are collapsed rather than preserved.
//
// 10 is the floor: below that, dense numeric tables stop being comfortably
// readable, which is most of what this product is.
//
// Enforced by theme.test.ts, which fails the build on any literal
// `fontSize:<n>` outside this scale. Marketing pages are exempt (see
// TYPE_SCALE_EXEMPT) — display type there is intentional.
export const TEXT = {
  micro:   10,  // dense meta: cost codes, badges, column labels
  small:   11,  // secondary labels, helper text
  body:    12,  // default UI text and table cells
  bodyLg:  13,  // emphasized body, primary values
  h3:      14,  // sub-section headings
  h2:      16,  // section headings
  h1:      20,  // page headings
  display: 24,  // page titles, key figures
  hero:    32,  // rare — headline figures only
} as const;

export const TYPE_SCALE: readonly number[] = Object.values(TEXT);

/** Files exempt from the app type scale — marketing/static prose. */
export const TYPE_SCALE_EXEMPT = [
  "LandingPage.tsx",
  "PricingPage.tsx",
  "SecurityPage.tsx",
  "PrivacyPolicyPage.tsx",
  "TermsPage.tsx",
] as const;

export const TRACKING = {
  display: "-0.035em",
  h2:      "-0.025em",
  h3:      "-0.01em",
  body:    "0",
  label:   "0.08em",
  eyebrow: "0.14em",
} as const;

export const LH = {
  display: 1.08,
  heading: 1.15,
  body:    1.65,
  tight:   1.4,
  tabular: 1.3,
} as const;

export const FONT = {
  sans:    "'Inter', 'Avenir Next', 'Century Gothic', system-ui, sans-serif",
  mono:    "'JetBrains Mono', ui-monospace, 'SF Mono', Consolas, monospace",
  display: "'Jost', 'Inter', sans-serif",
} as const;

// ── Numeric / money style objects (use inline: style={{...T.numeric}}) ─────
export const NUMERIC: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1, "zero" 1',
};
export const MONEY: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1',
  fontWeight: 700,
  letterSpacing: "-0.01em",
};

// ── Accent palette (same hex in both themes — use tealInk on light bg) ─────
//
// tealInk was #0a8f83, which is 3.35:1 on parchment -- below AA at every text
// size, in a constant whose own comment tells you to use it on light
// backgrounds. Nothing reads ACCENT_PALETTE today, so it was not a live
// failure; it was a loaded gun for whoever reached for it next. Now the same
// value the light theme actually uses, and theme.test.ts holds it there.
export const ACCENT_PALETTE = {
  teal:    "#4fd1c5",
  tealInk: "#057068",
  amber:   "#f6ad55",
  amber2:  "#d4a76a",
  green:   "#68d391",
  red:     "#fc8181",
  redDeep: "#d4706a",
  blue:    "#7ab8d4",
  yellow:  "#e2c96e",
  purple:  "#b794f4",
  pink:    "#f687b3",
} as const;

// ── Cost-status palette (single source) ───────────────────────────────────
// Status hues are theme-independent: they are signal, not surface, and must
// read identically in dark and light. Per-theme row and badge *backgrounds*
// live in Theme.costStatus; the hue and its legible text variant live here.
//
// This is the only place a status color may be defined. Re-declaring these
// values in a component is how the same concept ends up three different
// greens — see DESIGN.md, "The Status-Is-Not-Decoration Rule".
export const STATUS_PALETTE = {
  "Hard Bid":     { hue: "#4abf6a", text: "#7ad49a", label: "Hard Bid" },
  "Allowance":    { hue: "#4a8fbf", text: "#7ab8d4", label: "Allowance" },
  "Needs Review": { hue: "#bfb04a", text: "#d4c87a", label: "Needs Review" },
  "By Others":    { hue: "#888888", text: "#aaaaaa", label: "By Others" },
  "Excluded":     { hue: "#bf6a4a", text: "#e8a07a", label: "Excluded" },
  "Other":        { hue: "#666888", text: "#9999bb", label: "Other (unset)" },
} as const;

export type StatusKey = keyof typeof STATUS_PALETTE;

// ── Theme interface ────────────────────────────────────────────────────────
export interface Theme {
  mode: "dark" | "light";

  // ── Backward-compat flat keys (used throughout existing codebase) ─────────
  // Core backgrounds
  bgApp: string;
  bgPanel: string;
  bgHeader: string;
  bgSidebar: string;
  bgInput: string;
  bgInputFocus: string;
  bgModal: string;
  bgModalBackdrop: string;
  bgToast: string;
  bgHover: string;
  bgRowHover: string;
  bgScrollTrack: string;
  bgScrollThumb: string;

  // Accent layered backgrounds (teal tints)
  bgAccentFaint: string;
  bgAccentSubtle: string;
  bgAccentLight: string;
  bgAccentMedium: string;
  bgSelection: string;

  // Borders
  borderPrimary: string;
  borderSecondary: string;
  borderHover: string;
  borderAccent: string;
  borderAccentFaint: string;
  borderDashed: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  textWeak: string;
  textPlaceholder: string;
  textSubLabel: string;
  textInverse: string;
  textOnGold: string;

  // Semantic value colors (flat)
  positive: string;
  negative: string;

  // Cost status
  costStatus: {
    Allowance:      { rowBg: string; leftBar: string; badgeBg: string; badgeText: string };
    "Hard Bid":     { rowBg: string; leftBar: string; badgeBg: string; badgeText: string };
    "Needs Review": { rowBg: string; leftBar: string; badgeBg: string; badgeText: string };
    "By Others":    { rowBg: string; leftBar: string; badgeBg: string; badgeText: string };
    "Excluded":     { rowBg: string; leftBar: string; badgeBg: string; badgeText: string };
  };

  // Alt type styles
  altStyle: {
    Alt:  { rowBg: string; leftBar: string; badge: string; badgeText: string };
    Ded:  { rowBg: string; leftBar: string; badge: string; badgeText: string };
    Void: { rowBg: string; leftBar: string; badge: string; badgeText: string };
  };

  // Proposal color palette (6 slots)
  proposalColors: Array<{ bg: string; text: string; bar: string }>;

  // Analysis comparison
  versionA: { bg: string; bar: string };
  versionB: { bg: string; bar: string };

  // Danger zone
  dangerBg: string;
  dangerBorder: string;
  dangerText: string;
  dangerHoverBg: string;

  // Misc
  saveIndicator: string;
  divisionTabActive: string;
  divisionTabInactive: string;
  resizeHandle: string;

  // ── Design-system grouped tokens (new) ───────────────────────────────────
  accent: typeof ACCENT_PALETTE;

  shadow: {
    sm:       string;
    md:       string;
    lg:       string;
    xl:       string;
    glowTeal: string;
    dropTeal: string;
  };

  // Scale constants (identical in both themes — reference via T or import directly)
  radius:   typeof RADIUS;
  space:    typeof SPACE;
  font:     typeof FONT;
  size:     typeof SIZE;
  tracking: typeof TRACKING;
  lh:       typeof LH;

  // Style objects for numeric / money rendering
  numeric: CSSProperties;
  money:   CSSProperties;
}

// ── DARK THEME (default) ──────────────────────────────────────────────────
export const DARK_THEME: Theme = {
  mode: "dark",

  bgApp:            "#0f1117",
  bgPanel:          "#13151e",
  bgHeader:         "#161922",
  bgSidebar:        "#13151e",
  bgInput:          "#1a1d28",
  bgInputFocus:     "#1f2235",
  bgModal:          "#1a1d28",
  bgModalBackdrop:  "#00000080",
  bgToast:          "#1e2130",
  bgHover:          "#ffffff07",
  bgRowHover:       "#ffffff04",
  bgScrollTrack:    "#161922",
  bgScrollThumb:    "#2e3248",

  bgAccentFaint:    "#4fd1c50c",
  bgAccentSubtle:   "#4fd1c515",
  bgAccentLight:    "#4fd1c522",
  bgAccentMedium:   "#4fd1c528",
  bgSelection:      "#4fd1c512",

  borderPrimary:    "#22253a",
  borderSecondary:  "#2a2d3e",
  borderHover:      "#3a3d50",
  borderAccent:     "#4fd1c5",
  borderAccentFaint:"#4fd1c518",
  borderDashed:     "#3a3d50",

  textPrimary:      "#e8e6e1",
  textSecondary:    "#e0ddd8",
  textTertiary:     "#a0a8b8",
  textMuted:        "#8da0ad",
  textWeak:         "#80899a",
  textPlaceholder:  "#7c8e9a",
  textSubLabel:     "#c8c4bc",
  textInverse:      "#e8e6e1",
  textOnGold:       "#0f1117",

  positive: "#a0c8a0",
  negative: "#d4706a",

  costStatus: {
    Allowance:      { rowBg:"#1a3a5c30", leftBar:"#4a8fbf", badgeBg:"#1a3a5c60", badgeText:"#7ab8d4" },
    "Hard Bid":     { rowBg:"#1a3d2230", leftBar:"#4abf6a", badgeBg:"#1a3d2260", badgeText:"#7ad49a" },
    "Needs Review": { rowBg:"#3d3a1a30", leftBar:"#bfb04a", badgeBg:"#3d3a1a60", badgeText:"#d4c87a" },
    "By Others":    { rowBg:"#2a2a2a28", leftBar:"#888888", badgeBg:"#33333355", badgeText:"#aaaaaa" },
    "Excluded":     { rowBg:"#3d1a1a28", leftBar:"#bf6a4a", badgeBg:"#3d2a1a55", badgeText:"#e8a07a" },
  },

  altStyle: {
    Alt:  { rowBg:"#1a2a3d30", leftBar:"#6a9fbf", badge:"#6a9fbf", badgeText:"#aad4f0" },
    Ded:  { rowBg:"#2d1a2d30", leftBar:"#bf6abf", badge:"#bf6abf", badgeText:"#f0aaf0" },
    Void: { rowBg:"#2d2a1a30", leftBar:"#8a8060", badge:"#8a8060", badgeText:"#d0c8a0" },
  },

  proposalColors: [
    { bg:"#7ab8d415", text:"#7ab8d4", bar:"#7ab8d4" },
    { bg:"#d4a76a15", text:"#d4a76a", bar:"#d4a76a" },
    { bg:"#a0c8a015", text:"#a0c8a0", bar:"#a0c8a0" },
    { bg:"#c8a0d415", text:"#c8a0d4", bar:"#c8a0d4" },
    { bg:"#d4706a15", text:"#d4706a", bar:"#d4706a" },
    { bg:"#6ad4c815", text:"#6ad4c8", bar:"#6ad4c8" },
  ],

  versionA: { bg:"#1a2a3a", bar:"#4a8fbf" },
  versionB: { bg:"#1a3a2a", bar:"#4abf6a" },

  dangerBg:       "#3a1a1a",
  dangerBorder:   "#5a2020",
  dangerText:     "#d08080",
  dangerHoverBg:  "#4a2020",

  saveIndicator:       "#4abf6a",
  divisionTabActive:   "#4fd1c5",
  divisionTabInactive: "#a0a8b8",
  resizeHandle:        "#4a5060",

  // ── Design-system grouped tokens ─────────────────────────────────────────
  accent: ACCENT_PALETTE,

  shadow: {
    sm:       "0 1px 2px rgba(0,0,0,.4)",
    md:       "0 4px 16px rgba(0,0,0,.4)",
    lg:       "0 12px 32px rgba(0,0,0,.4)",
    xl:       "0 24px 60px rgba(0,0,0,.5)",
    glowTeal: "0 0 24px 6px rgba(79,209,197,.35)",
    dropTeal: "0 24px 60px rgba(79,209,197,.18)",
  },

  radius:   RADIUS,
  space:    SPACE,
  font:     FONT,
  size:     SIZE,
  tracking: TRACKING,
  lh:       LH,
  numeric:  NUMERIC,
  money:    MONEY,
};

// ── LIGHT THEME ("Warm Technical") ───────────────────────────────────────
// Warm parchment body, dark header anchor, teal brand signature.
// Design principles:
//   • Header stays dark — visual anchor, brand continuity across themes
//   • Warm off-whites (#f3ede3) — never cold/clinical pure white
//   • Near-black ink text (#1c1810) — maximum legibility
//   • Teal accent darkened (#0a8f83) for legibility on light backgrounds
export const LIGHT_THEME: Theme = {
  mode: "light",

  bgApp:            "#f0ebe0",
  bgPanel:          "#faf7f2",
  bgHeader:         "#1f2235",  // DARK — intentional design anchor
  bgSidebar:        "#e8e2d5",
  bgInput:          "#ffffff",
  bgInputFocus:     "#fffdf8",
  bgModal:          "#faf7f2",
  bgModalBackdrop:  "#1c1810a0",
  bgToast:          "#2a2838",
  bgHover:          "#00000008",
  bgRowHover:       "#00000005",
  bgScrollTrack:    "#e8e2d5",
  bgScrollThumb:    "#c8bfb0",

  bgAccentFaint:    "#0a8f830e",
  bgAccentSubtle:   "#0a8f8318",
  bgAccentLight:    "#0a8f8328",
  bgAccentMedium:   "#0a8f8335",
  bgSelection:      "#0a8f8318",

  borderPrimary:    "#d5cfc4",
  borderSecondary:  "#e0d9cf",
  borderHover:      "#b8b0a0",
  // Darkened from #0a8f83, which was 3.35:1 on parchment -- below AA for
  // every size of text, and this is the colour the product's own name is
  // set in. 5.01:1 on bgApp, 5.58:1 on bgPanel. Enforced by theme.test.ts.
  borderAccent:     "#057068",
  borderAccentFaint:"#0a8f8330",
  borderDashed:     "#c0b8a8",

  textPrimary:      "#1c1810",
  textSecondary:    "#3a342c",
  textTertiary:     "#6a6055",
  textMuted:        "#5a5349",
  textWeak:         "#70655a",
  textPlaceholder:  "#6a655b",
  textSubLabel:     "#5a5248",
  textInverse:      "#e8e4de",
  textOnGold:       "#ffffff",

  positive: "#1e7835",
  negative: "#c02828",

  costStatus: {
    Allowance:      { rowBg:"#e6f0f8", leftBar:"#2878b8", badgeBg:"#c8dff0", badgeText:"#0e4a78" },
    "Hard Bid":     { rowBg:"#e8f5eb", leftBar:"#28984a", badgeBg:"#c0e8cc", badgeText:"#0e4a28" },
    "Needs Review": { rowBg:"#faf3df", leftBar:"#c8940a", badgeBg:"#f0e098", badgeText:"#5a3e00" },
    "By Others":    { rowBg:"#f0f0f0", leftBar:"#888888", badgeBg:"#dddddd", badgeText:"#555555" },
    "Excluded":     { rowBg:"#fff0e8", leftBar:"#c85a28", badgeBg:"#fad8c0", badgeText:"#7a2800" },
  },

  altStyle: {
    Alt:  { rowBg:"#e8f0f8", leftBar:"#3a7aaa", badge:"#3a7aaa", badgeText:"#0a3a60" },
    Ded:  { rowBg:"#f7ecf7", leftBar:"#8f3f8f", badge:"#8f3f8f", badgeText:"#5a1a5a" },
    Void: { rowBg:"#f5f0e5", leftBar:"#8a7850", badge:"#8a7850", badgeText:"#3a3010" },
  },

  proposalColors: [
    { bg:"#e2f0f8", text:"#1a5c80", bar:"#2e88b8" },
    { bg:"#f8f0e2", text:"#8a4e10", bar:"#c88030" },
    { bg:"#e8f5e8", text:"#1a5830", bar:"#389850" },
    { bg:"#f2e8f8", text:"#5a28a0", bar:"#8858c8" },
    { bg:"#f8e8e8", text:"#8a1e1e", bar:"#c04848" },
    { bg:"#e4f5f2", text:"#0e5e58", bar:"#2898a8" },
  ],

  versionA: { bg:"#deeaf5", bar:"#2878b8" },
  versionB: { bg:"#dff0e5", bar:"#28984a" },

  dangerBg:       "#fdf0f0",
  dangerBorder:   "#e08080",
  dangerText:     "#c02828",
  dangerHoverBg:  "#fce0e0",

  saveIndicator:       "#28984a",
  divisionTabActive:   "#0a8f83",
  divisionTabInactive: "#6a6055",
  resizeHandle:        "#b8b0a0",

  // ── Design-system grouped tokens ─────────────────────────────────────────
  accent: ACCENT_PALETTE,

  shadow: {
    sm:       "0 1px 2px rgba(28,24,16,.08)",
    md:       "0 4px 16px rgba(28,24,16,.10)",
    lg:       "0 12px 32px rgba(28,24,16,.14)",
    xl:       "0 24px 60px rgba(28,24,16,.18)",
    glowTeal: "0 0 24px 6px rgba(10,143,131,.25)",
    dropTeal: "0 24px 60px rgba(10,143,131,.14)",
  },

  radius:   RADIUS,
  space:    SPACE,
  font:     FONT,
  size:     SIZE,
  tracking: TRACKING,
  lh:       LH,
  numeric:  NUMERIC,
  money:    MONEY,
};
